import { useState, useCallback } from "react";

interface UploadMetadata {
  name: string;
  size: number;
  contentType: string;
}

interface UploadResponse {
  uploadURL: string;
  objectPath: string;
  metadata: UploadMetadata;
}

interface UseUploadOptions {
  onSuccess?: (response: UploadResponse) => void;
  onError?: (error: Error) => void;
}

/**
 * Detects whether a response body is a Cloudflare bot-challenge HTML page.
 * CF bot-challenge / managed-challenge pages are served with content-type
 * text/html and begin with <!DOCTYPE html> … "Just a moment…".
 */
function isCloudflareChallengeBody(text: string): boolean {
  const trimmed = text.trimStart();
  return (
    trimmed.startsWith('<!DOCTYPE') &&
    (trimmed.includes('just a moment') ||
      trimmed.includes('Just a moment') ||
      trimmed.includes('cf-browser-verification') ||
      trimmed.includes('cloudflare'))
  );
}

export function useUpload(options: UseUploadOptions = {}) {
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [progress, setProgress] = useState(0);

  const uploadFile = useCallback(
    async (file: File): Promise<UploadResponse | null> => {
      // Reject oversized videos before any network call (Cloudflare Pro cap is 100 MB)
      const MAX_VIDEO_SIZE = 100 * 1024 * 1024;
      if (file.type.startsWith('video/') && file.size > MAX_VIDEO_SIZE) {
        const sizeMB = (file.size / (1024 * 1024)).toFixed(1);
        const err = new Error(
          `Video is ${sizeMB} MB — videos must be under 100 MB. Please trim or compress the clip.`
        );
        setError(err);
        options.onError?.(err);
        return null;
      }

      // Token resolution: prefer partner session token → stored Eatzy token → local JWT
      // The partner session token (sessionStorage) takes priority because partners log in
      // via the Eatzy restaurant auth flow, not the local JWT flow.
      const localJwt = window.localStorage.getItem("auth_token");
      const eatzyToken = window.localStorage.getItem("eatzyUserToken");
      const partnerToken = window.sessionStorage.getItem("partnerEatzyToken");
      const effectiveEatzyToken = partnerToken || eatzyToken;

      // Early-reject: if neither a local JWT nor an Eatzy token is present the
      // request would arrive at the server without any auth header. Cloudflare's WAF
      // treats unauthenticated multipart-file POSTs as suspicious and returns a
      // 403 bot-challenge HTML page that would surface as a generic "Upload failed"
      // error. Reject early with a clear message instead.
      if (!localJwt && !effectiveEatzyToken) {
        const err = new Error(
          "Session expired — please log in again to upload files."
        );
        setError(err);
        options.onError?.(err);
        return null;
      }

      setIsUploading(true);
      setError(null);
      setProgress(0);

      try {
        setProgress(10);

        const formData = new FormData();
        formData.append('file', file);

        const headers: Record<string, string> = {};
        if (localJwt) headers['Authorization'] = `Bearer ${localJwt}`;
        if (effectiveEatzyToken) headers['x-eatzy-token'] = effectiveEatzyToken;

        setProgress(30);

        const response = await fetch('/api/media/upload-cdn', {
          method: 'POST',
          credentials: 'include',
          headers,
          body: formData,
        });

        if (!response.ok) {
          // Check content-type before attempting JSON parse.
          const contentType = response.headers.get('content-type') || '';

          if (contentType.includes('text/html')) {
            // Cloudflare WAF / bot-challenge pages are served as HTML with a 4xx
            // status. The JSON parse below would silently swallow the error and
            // surface a confusing "Upload failed" message. Show a clear message
            // instead so partners know to contact support or try again.
            //
            // OPERATOR NOTE — if this error appears in production:
            //   Cloudflare Dashboard → Security → WAF → Custom rules
            //   Add a "Skip" rule for: URI Path equals /api/media/upload-cdn
            //   Apply to: Bot Fight Mode / Managed Challenge / any matching rule.
            //   This lets authenticated browser requests reach Express without
            //   triggering the bot challenge on multipart file uploads.
            const bodyText = await response.text().catch(() => '');
            const isChallenge = isCloudflareChallengeBody(bodyText);
            const msg = isChallenge
              ? "Upload blocked by network security — please try again. If the problem persists, contact support. (CF-challenge)"
              : `Upload failed — server returned ${response.status}`;
            throw new Error(msg);
          }

          const errorData = await response.json().catch(() => ({}));
          if (response.status === 413) {
            throw new Error(errorData.error || 'File too large — please keep videos under 100 MB');
          }
          throw new Error(errorData.error || 'Failed to upload file');
        }

        const result = await response.json();
        if (!result.url) {
          throw new Error('Upload succeeded but no URL was returned');
        }
        const cdnUrl: string = result.url;

        const uploadResponse: UploadResponse = {
          uploadURL: '',
          objectPath: cdnUrl,
          metadata: {
            name: file.name,
            size: file.size,
            contentType: file.type || 'application/octet-stream',
          },
        };

        setProgress(100);
        options.onSuccess?.(uploadResponse);
        return uploadResponse;
      } catch (err) {
        const error = err instanceof Error ? err : new Error("Upload failed");
        setError(error);
        options.onError?.(error);
        return null;
      } finally {
        setIsUploading(false);
      }
    },
    [options]
  );

  return {
    uploadFile,
    isUploading,
    error,
    progress,
  };
}
